import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, Label } from '../design-system';
import { forgotPassword } from '../services/consentApi';

/**
 * Forgot Password page - allows users to request a password reset email.
 * Route: /forgot-password
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await forgotPassword(email.trim());
      setSuccess(true);
    } catch (err: any) {
      // Show generic message for security - don't reveal if email exists
      // The backend should also return the same message regardless of email existence
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="ds-container" aria-labelledby="forgot-password-title">
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <Card>
          <header className="ds-card__header" style={{ alignItems: 'flex-start' }}>
            <div>
              <p className="ds-text-muted" style={{ margin: 0, fontWeight: 600 }}>Consent Platform</p>
              <h1 id="forgot-password-title" className="ds-heading-xl">Forgot your password?</h1>
              <p className="ds-text-muted" style={{ marginTop: 6 }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>
          </header>

          {success ? (
            <div className="ds-card__body">
              <div className="ds-alert ds-alert--success" role="status">
                <strong>Check your email</strong>
                <p style={{ margin: '8px 0 0' }}>
                  If an account exists with this email, you'll receive a password reset link shortly.
                  The link will expire in 24 hours.
                </p>
              </div>
              <div style={{ marginTop: 24 }}>
                <Link to="/login" className="ds-link" style={{ color: 'var(--ds-accent)' }}>
                  ← Back to Sign In
                </Link>
              </div>
            </div>
          ) : (
            <form className="ds-form" onSubmit={handleSubmit} noValidate aria-describedby={error ? 'forgot-error' : undefined}>
              {error && (
                <div className="ds-alert ds-alert--error" id="forgot-error" role="alert">
                  {error}
                </div>
              )}

              <div className="ds-field">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}

          <footer className="ds-card__footer">
            <p className="ds-text-muted" style={{ margin: 0, textAlign: 'center' }}>
              Remember your password?{' '}
              <Link to="/login" style={{ color: 'var(--ds-accent)' }}>
                Sign in
              </Link>
            </p>
          </footer>
        </Card>
      </div>
    </main>
  );
};

export default ForgotPassword;
